// Editor de medios de pago de un cobro de tesorería: varias filas (parte transferencia, parte cheque…),
// con las mismas reglas del mesón (comun/medios.ts). Formato como el «Registro de Pagos» de Random
// (pedido de Rodrigo, 2026-10-16/17): cuatro medios —efectivo, transferencia, TARJETA (débito o
// crédito, por POS Getnet o link de pago) y cheque— y una grilla Lín · TD · Medio · N° documento ·
// Banco · Fecha · Cuotas · Monto, con el total. La transferencia lleva su fecha (puede entrar otro día).
import type { ReactNode } from 'react';
import type { MedioCaja } from '../api/contratos';
import { pesos } from '../comun/formato';
import { entero, filaNueva, problemaDe, type Fila, type Terminal } from '../comun/medios';
import { hoyIso } from '../ventas/comercial';
import { SelectorBanco } from '../comun/bancos';

type Grupo = 'EFECTIVO' | 'TRANSFERENCIA' | 'TARJETA' | 'CHEQUE';
const grupoDe = (m: MedioCaja): Grupo => (m === 'DEBITO' || m === 'CREDITO' ? 'TARJETA' : m);
const NOMBRE_GRUPO: Record<Grupo, string> = {
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA: 'Transferencia',
  TARJETA: 'Tarjeta',
  CHEQUE: 'Cheque',
};
const GRUPOS: Grupo[] = ['EFECTIVO', 'TRANSFERENCIA', 'TARJETA', 'CHEQUE'];

/** Código del medio como en Random (EFV efectivo, ATB transferencia, TDV/TJV tarjeta, CHV cheque). */
const TD: Record<MedioCaja, string> = {
  EFECTIVO: 'EFV',
  TRANSFERENCIA: 'ATB',
  DEBITO: 'TDV',
  CREDITO: 'TJV',
  CHEQUE: 'CHV',
};

// Íconos dibujados (no emoji): mismo tamaño y alineados con el texto en cualquier equipo.
export const trazo = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};
export const ICONO: Record<Grupo, ReactNode> = {
  EFECTIVO: (
    <svg {...trazo}>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 9v.01M18 15v.01" />
    </svg>
  ),
  TRANSFERENCIA: (
    <svg {...trazo}>
      <path d="M3 10h18L12 4 3 10z" />
      <path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" />
    </svg>
  ),
  TARJETA: (
    <svg {...trazo}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20M6 15h4" />
    </svg>
  ),
  CHEQUE: (
    <svg {...trazo}>
      <rect x="2" y="6" width="20" height="12" rx="1.5" />
      <path d="M5 10h8M5 14h5M15 14l2 0 2-3" />
    </svg>
  ),
};

const medioDeGrupo = (g: Grupo, actual?: MedioCaja): MedioCaja =>
  g === 'TARJETA' ? (actual === 'CREDITO' ? 'CREDITO' : 'DEBITO') : g;

export function EditorMedios({
  filas,
  alCambiar,
  sugerido,
}: {
  filas: Fila[];
  alCambiar: (filas: Fila[]) => void;
  /** Lo marcado en el estado de cuenta: «Completar» deja el pago justo en ese monto. */
  sugerido?: number | undefined;
}) {
  const poner = (i: number, c: Partial<Fila>) =>
    alCambiar(filas.map((f, j) => (j === i ? { ...f, ...c } : f)));
  const total = filas.reduce((s, f) => s + (entero(f.monto) || 0), 0);
  const falta = sugerido !== undefined ? sugerido - total : 0;
  const nueva = (m: MedioCaja, monto = '') => ({
    ...filaNueva(monto, m),
    ...(m === 'TRANSFERENCIA' ? { fecha: hoyIso() } : {}),
  });
  const cambiarMedio = (i: number, m: MedioCaja) =>
    poner(i, {
      medio: m,
      ...(m === 'TRANSFERENCIA' && !filas[i]!.fecha ? { fecha: hoyIso() } : {}),
    });

  // Botón de medio: cambia la última fila si aún no tiene monto; si no, agrega otra con ese medio.
  const elegir = (g: Grupo) => {
    const i = filas.length - 1;
    const u = filas[i];
    if (u && !u.monto) cambiarMedio(i, medioDeGrupo(g, u.medio));
    else if (filas.length < 6)
      alCambiar([...filas, nueva(medioDeGrupo(g), falta > 0 ? String(falta) : '')]);
  };
  const completar = () => {
    if (!sugerido) return;
    const otras = filas.slice(0, -1).reduce((s, f) => s + (entero(f.monto) || 0), 0);
    poner(filas.length - 1, { monto: String(Math.max(0, sugerido - otras)) });
  };
  const activo = filas.length ? grupoDe(filas[filas.length - 1]!.medio) : null;

  return (
    <div className="editor-medios">
      <div className="botones-medio" role="group" aria-label="Elegir medio de pago">
        {GRUPOS.map((g) => (
          <button
            key={g}
            type="button"
            className={activo === g ? 'activo' : ''}
            aria-pressed={activo === g}
            onClick={() => elegir(g)}
          >
            {ICONO[g]}
            <span>{NOMBRE_GRUPO[g]}</span>
          </button>
        ))}
      </div>
      <table className="grilla compacta cobro-medios" aria-label="Medios de pago">
        <thead>
          <tr>
            <th className="der">Lín</th>
            <th>TD</th>
            <th>Medio</th>
            <th>N° documento / operación</th>
            <th>Banco</th>
            <th>Fecha</th>
            <th>Cuotas</th>
            <th className="der">Monto</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => {
            const problema = f.monto ? problemaDe(f) : null;
            const g = grupoDe(f.medio);
            return (
              <tr key={i} data-medio={f.medio} className={problema ? 'con-problema' : ''}>
                <td className="der mono">{i + 1}</td>
                <td>
                  <span className="td-medio mono">{TD[f.medio]}</span>
                </td>
                <td>
                  <div className="medio-celda">
                    <select
                      aria-label={`Medio ${i + 1}`}
                      value={g === 'TARJETA' ? 'TARJETA' : f.medio}
                      onChange={(e) =>
                        cambiarMedio(i, medioDeGrupo(e.target.value as Grupo, f.medio))
                      }
                    >
                      {GRUPOS.map((x) => (
                        <option key={x} value={x}>
                          {NOMBRE_GRUPO[x]}
                        </option>
                      ))}
                    </select>
                    {g === 'TARJETA' && (
                      <>
                        <select
                          aria-label={`Tipo de tarjeta ${i + 1}`}
                          value={f.medio}
                          onChange={(e) => poner(i, { medio: e.target.value as MedioCaja })}
                        >
                          <option value="DEBITO">Débito</option>
                          <option value="CREDITO">Crédito</option>
                        </select>
                        <select
                          aria-label={`Canal de la tarjeta ${i + 1}`}
                          title="POS Getnet del mesón (entra al cierre de caja) o link de pago enviado al cliente. El pago en línea del portal B2B se registrará solo."
                          value={f.terminal}
                          onChange={(e) => poner(i, { terminal: e.target.value as Terminal })}
                        >
                          <option value="GETNET">POS Getnet</option>
                          <option value="LINK_PAGO">Link de pago</option>
                        </select>
                      </>
                    )}
                  </div>
                </td>
                <td>
                  {g === 'TARJETA' && (
                    <input
                      aria-label={`Autorización ${i + 1}`}
                      placeholder="código de autorización"
                      value={f.autorizacion}
                      onChange={(e) => poner(i, { autorizacion: e.target.value })}
                    />
                  )}
                  {f.medio === 'TRANSFERENCIA' && (
                    <input
                      aria-label={`Operación ${i + 1}`}
                      placeholder="N° de operación"
                      value={f.referencia}
                      onChange={(e) => poner(i, { referencia: e.target.value })}
                    />
                  )}
                  {f.medio === 'CHEQUE' && (
                    <input
                      aria-label={`N° cheque ${i + 1}`}
                      placeholder="N° de cheque"
                      value={f.numero}
                      onChange={(e) => poner(i, { numero: e.target.value })}
                    />
                  )}
                  {f.medio === 'EFECTIVO' && <span className="apagado">—</span>}
                </td>
                <td>
                  {f.medio === 'CHEQUE' || f.medio === 'TRANSFERENCIA' ? (
                    <SelectorBanco
                      etiqueta={`Banco ${i + 1}`}
                      vacio={f.medio === 'CHEQUE' ? 'Banco del cheque…' : 'Banco (opcional)'}
                      valor={f.banco}
                      alCambiar={(v) => poner(i, { banco: v })}
                    />
                  ) : (
                    <span className="apagado">—</span>
                  )}
                </td>
                <td>
                  {f.medio === 'CHEQUE' && (
                    <input
                      aria-label={`Fecha cheque ${i + 1}`}
                      type="date"
                      title="Fecha del cheque (al día o a fecha)"
                      value={f.fecha}
                      onChange={(e) => poner(i, { fecha: e.target.value })}
                    />
                  )}
                  {f.medio === 'TRANSFERENCIA' && (
                    <input
                      aria-label={`Fecha transferencia ${i + 1}`}
                      type="date"
                      title="Fecha en que entró la transferencia (puede ser otro día que hoy)"
                      value={f.fecha}
                      onChange={(e) => poner(i, { fecha: e.target.value })}
                    />
                  )}
                  {f.medio !== 'CHEQUE' && f.medio !== 'TRANSFERENCIA' && (
                    <span className="apagado">hoy</span>
                  )}
                </td>
                <td>
                  {f.medio === 'CREDITO' ? (
                    <input
                      aria-label={`Cuotas ${i + 1}`}
                      className="celda-num"
                      inputMode="numeric"
                      placeholder="1"
                      value={f.cuotas}
                      onChange={(e) => poner(i, { cuotas: e.target.value.replace(/\D/g, '') })}
                    />
                  ) : (
                    <span className="apagado">—</span>
                  )}
                </td>
                <td className="der">
                  <input
                    aria-label={`Monto ${i + 1}`}
                    className="celda-num fuerte"
                    inputMode="numeric"
                    placeholder="$ 0"
                    value={f.monto}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => poner(i, { monto: e.target.value })}
                  />
                  {problema && <div className="cobro-falta">falta: {problema}</div>}
                </td>
                <td>
                  {filas.length > 1 && (
                    <button
                      type="button"
                      className="boton-icono"
                      aria-label={`Quitar medio ${i + 1}`}
                      onClick={() => alCambiar(filas.filter((_, j) => j !== i))}
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>
              <button
                type="button"
                disabled={filas.length >= 6}
                onClick={() => alCambiar([...filas, nueva('TRANSFERENCIA')])}
              >
                + Otro medio
              </button>
            </td>
            <td colSpan={4} className="der">
              {sugerido !== undefined && sugerido > 0 && falta !== 0 && (
                <button
                  type="button"
                  title="Deja el pago justo en lo marcado en el estado de cuenta"
                  onClick={completar}
                >
                  Completar con lo marcado ({pesos(String(sugerido))})
                </button>
              )}{' '}
              <b>Total recibido</b>
            </td>
            <td className="der total-medios">
              <b>{pesos(String(total))}</b>
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
